import { ReactNode } from 'react';
import BrandIcon, { BrandMark } from '@/components/BrandIcon';

export interface CopyToastProps {
  /** A contact channel's own mark. Omit when passing `icon` instead. */
  mark?: BrandMark;
  /**
   * An icon for something that ISN'T one of the four contact brands — a meeting
   * link, say, whose provider mark is a hosted image rather than a path in
   * BrandIcon. BrandIcon is deliberately closed to official contact marks (see
   * its header); this is the escape hatch that keeps it that way.
   */
  icon?: ReactNode;
  label: string;
  value: string;
}

/**
 * "LinkedIn copied", and the value underneath it.
 *
 * A copy confirmation answers two questions — did it work, and what exactly is on
 * my clipboard now — so it's built rather than sentence-formatted: the label
 * leads at body size, the value sits under it in the tone this app uses for meta.
 *
 * The value stays on one line and clips. A copied URL can be 200 characters, and
 * a toast that reflows to four lines to prove it just shoves everything under it
 * down the screen — the point is recognition, not transcription. It's on the
 * clipboard either way, and the full string is in the title.
 */
const CopyToast = ({ label, value }: Omit<CopyToastProps, 'mark' | 'icon'>) => (
  <span className="toast-copy">
    <span className="toast-copy__label">{label} copied</span>
    <span className="toast-copy__value" title={value}>
      {value}
    </span>
  </span>
);

/**
 * The icon is the MARK OF THE THING COPIED rather than a generic green tick.
 * After clicking LinkedIn and then email, the logo says which toast is which
 * before you've read either of them.
 */
export const CopyToastIcon = ({ mark, icon }: Pick<CopyToastProps, 'mark' | 'icon'>) => {
  if (icon) return <span className="toast-copy__mark">{icon}</span>;
  return mark ? <BrandIcon mark={mark} className="toast-copy__mark" /> : null;
};

export default CopyToast;
